import React from 'react';

/**
 * 키워드 수정 모달 컴포넌트
 * @param {Object} props
 * @param {boolean} props.isOpen - 모달 표시 여부
 * @param {Object} props.modalData - 모달 데이터 (campaigns, placeName, placeId)
 * @param {Array} props.changes - 변경된 키워드 목록
 * @param {boolean} props.loading - 저장 중 여부
 * @param {Function} props.onKeywordChange - 키워드 변경 핸들러
 * @param {Function} props.onAddNew - 새 키워드 추가 핸들러
 * @param {Function} props.onUpdateNew - 새 키워드 값 업데이트 핸들러
 * @param {Function} props.onRemoveNew - 새 키워드 삭제 핸들러
 * @param {Function} props.onKeywordClick - 키워드 클릭 핸들러 (검색 열기)
 * @param {Function} props.onSave - 저장 핸들러
 * @param {Function} props.onClose - 닫기 핸들러
 * @param {Function} props.getChangeForKeyword - 특정 키워드의 변경사항 조회
 * @param {Function} props.getNewKeywordsForCampaign - 특정 캠페인의 새 키워드 조회
 */
function KeywordEditModal({
  isOpen,
  modalData,
  changes = [],
  loading = false,
  onKeywordChange,
  onAddNew,
  onUpdateNew,
  onRemoveNew,
  onKeywordClick,
  onSave,
  onClose,
  getChangeForKeyword,
  getNewKeywordsForCampaign
}) {
  if (!isOpen) return null;

  const { campaigns = [], placeName, placeId } = modalData || {};

  return (
    <div className="modal-overlay">
      <div 
        className="modal-content" 
        style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'auto' }}
      >
        <h3>키워드 수정/생성 - {placeName}</h3>
        
        <div style={{ marginBottom: '20px' }}>
          <strong>플레이스 ID:</strong> {placeId}
        </div>
        
        {campaigns.map((campaign, campaignIdx) => (
          <div 
            key={campaign.adCampaignId || campaignIdx} 
            style={{ 
              marginBottom: '30px', 
              padding: '15px', 
              border: '1px solid #ddd', 
              borderRadius: '5px' 
            }}
          >
            <h4 style={{ margin: '0 0 15px 0', color: '#333' }}>
              캠페인 ID: {campaign.adCampaignId}
            </h4>
            
            {/* 기존 키워드 목록 */}
            {campaign.keywords && campaign.keywords.map((keyword, keywordIdx) => {
              const change = getChangeForKeyword?.(keyword.adKeywordId);
              return (
                <div 
                  key={keyword.adKeywordId || keywordIdx} 
                  className="keyword-edit-row"
                  style={{ 
                    marginBottom: '10px', 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '10px' 
                  }}
                >
                  <span className="keyword-edit-label" style={{ minWidth: '100px' }}>키워드 {keywordIdx + 1}:</span>
                  <input
                    type="text"
                    defaultValue={keyword.adKeyword}
                    readOnly
                    onClick={() => onKeywordClick?.(keyword.adKeyword)}
                    style={{ 
                      flex: 1, 
                      padding: '5px', 
                      backgroundColor: '#f5f5f5', 
                      cursor: 'pointer' 
                    }}
                  />
                  <span className="keyword-edit-arrow" style={{ minWidth: '60px', paddingTop: '5px', textAlign: 'center' }}>→</span>
                  <input
                    type="text"
                    placeholder="변경할 키워드"
                    defaultValue={change?.newKeyword || ''}
                    onChange={(e) => onKeywordChange?.(keyword.adKeywordId, e.target.value, keyword.adKeyword)}
                    style={{ flex: 1, padding: '5px' }}
                  />
                </div>
              );
            })}

            {/* 새 키워드 추가 영역 */}
            <div 
              style={{ 
                marginTop: '15px', 
                padding: '10px', 
                border: '1px solid #ddd', 
                borderRadius: '5px', 
                backgroundColor: '#f9f9f9' 
              }}
            >
              <button 
                onClick={() => onAddNew?.(campaign.adCampaignId)}
                style={{ 
                  marginBottom: '10px', 
                  backgroundColor: '#28a745', 
                  color: 'white', 
                  border: 'none', 
                  padding: '8px 16px', 
                  borderRadius: '4px', 
                  cursor: 'pointer' 
                }}
              >
                + 키워드 추가
              </button>
              
              {/* 새로 추가된 키워드 입력 필드들 */}
              {getNewKeywordsForCampaign?.(campaign.adCampaignId).map((change, idx) => (
                <div 
                  key={`new-${campaign.adCampaignId}-${idx}`} 
                  className="keyword-edit-row"
                  style={{ 
                    marginBottom: '10px', 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '10px' 
                  }}
                >
                  <span className="keyword-edit-label" style={{ minWidth: '100px', paddingTop: '5px' }}>새 키워드:</span>
                  <input
                    type="text"
                    placeholder="새 키워드를 입력하세요"
                    value={change.newKeyword || ''}
                    onChange={(e) => onUpdateNew?.(change, e.target.value)}
                    style={{ flex: 1, padding: '5px' }}
                  />
                  <button
                    onClick={() => onRemoveNew?.(change)}
                    style={{ 
                      backgroundColor: '#dc3545', 
                      color: 'white', 
                      border: 'none', 
                      padding: '5px 10px', 
                      borderRadius: '4px', 
                      cursor: 'pointer' 
                    }}
                  >
                    삭제
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
        
        {/* 버튼 영역 */}
        <div className="modal-buttons" style={{ marginTop: '20px', textAlign: 'right' }}>
          <button 
            onClick={onSave} 
            disabled={loading || changes.length === 0}
            style={{ 
              marginRight: '10px', 
              backgroundColor: '#007bff', 
              color: 'white', 
              border: 'none', 
              padding: '8px 16px', 
              borderRadius: '4px', 
              cursor: 'pointer' 
            }}
          >
            {loading ? '저장 중...' : '저장'}
          </button>
          <button 
            onClick={onClose}
            style={{ 
              backgroundColor: '#6c757d', 
              color: 'white', 
              border: 'none', 
              padding: '8px 16px', 
              borderRadius: '4px', 
              cursor: 'pointer' 
            }}
          >
            취소
          </button>
        </div>
      </div>
    </div>
  );
}

export default KeywordEditModal;
